import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isEntitlementActive } from '@/lib/features';
import { useOrgStore } from '@/stores/orgStore';
import type {
  CustomReportKey,
  OrganizationCustomReportEntitlement,
  OrganizationFeatureEntitlement,
  OrganizationFeatureKey,
} from '@/types/database.types';

interface EntitlementQueryOptions {
  enabled?: boolean;
}

export function useOrganizationFeatureEntitlements(
  organizationIds?: string[],
  options?: EntitlementQueryOptions,
) {
  const enabled =
    options?.enabled ?? (organizationIds === undefined || organizationIds.length > 0);

  return useQuery({
    queryKey: ['organization-feature-entitlements', organizationIds],
    enabled,
    queryFn: async () => {
      let query = supabase
        .from('organization_feature_entitlements')
        .select('*')
        .order('created_at', { ascending: false });

      if (organizationIds?.length) {
        query = query.in('organization_id', organizationIds);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as OrganizationFeatureEntitlement[];
    },
  });
}

export function useFeatureEntitlement(
  featureKey: OrganizationFeatureKey,
  organizationId?: string | null,
) {
  const activeOrganizationId = useOrgStore((s) => s.organization?.id);
  const resolvedOrganizationId = organizationId ?? activeOrganizationId;

  return useQuery({
    queryKey: ['feature-entitlement', resolvedOrganizationId, featureKey],
    enabled: !!resolvedOrganizationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_feature_entitlements')
        .select('*')
        .eq('organization_id', resolvedOrganizationId!)
        .eq('feature_key', featureKey)
        .maybeSingle();
      if (error) throw error;

      const entitlement = data as OrganizationFeatureEntitlement | null;
      return {
        entitlement,
        isEnabled: isEntitlementActive(entitlement),
      };
    },
  });
}

export function useCustomReportEntitlement(
  reportKey: CustomReportKey,
  organizationId?: string | null,
) {
  const activeOrganizationId = useOrgStore((s) => s.organization?.id);
  const resolvedOrganizationId = organizationId ?? activeOrganizationId;

  return useQuery({
    queryKey: ['custom-report-entitlement', resolvedOrganizationId, reportKey],
    enabled: !!resolvedOrganizationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_custom_report_entitlements')
        .select('*')
        .eq('organization_id', resolvedOrganizationId!)
        .eq('report_key', reportKey)
        .maybeSingle();
      if (error) throw error;

      const entitlement = data as OrganizationCustomReportEntitlement | null;
      return { entitlement, isEnabled: entitlement?.enabled === true };
    },
  });
}
